/**
 * Shared styled components for the 2-step transfer wizard shell.
 * Used by ClinicianTransferDrawer and ClientTransferDrawer.
 */

import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.backgrounds.page};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[4]};
`;

export const InnerContainer = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

export const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primary.base};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

export const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const PageSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StepIndicatorRow = styled.div`
  display: flex;
  align-items: center;
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
`;

export const StepBubble = styled.div<{ $status: 'active' | 'completed' | 'upcoming' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $status, theme }) =>
    $status === 'completed'
      ? `background: ${theme.colors.primary.base}; color: white;`
      : $status === 'active'
        ? `background: ${theme.colors.primary.base}; color: white; box-shadow: 0 0 0 4px ${theme.colors.primary.lightSky};`
        : `background: ${theme.colors.backgrounds.light}; color: ${theme.colors.text.secondary}; border: 2px solid ${theme.colors.borders.light};`}

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const StepLabel = styled.span<{ $active: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary.base : theme.colors.text.secondary};
  font-weight: ${({ $active, theme }) =>
    $active ? theme.typography.fontWeight.medium : theme.typography.fontWeight.normal};
  margin-left: ${({ theme }) => theme.spacing[2]};
  margin-right: ${({ theme }) => theme.spacing[4]};
`;

export const StepConnector = styled.div<{ $done: boolean }>`
  width: 48px;
  height: 2px;
  background: ${({ $done, theme }) =>
    $done ? theme.colors.primary.base : theme.colors.borders.light};
  margin: 0 ${({ theme }) => theme.spacing[2]};
  flex-shrink: 0;
`;

export const StepTag = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  span {
    color: ${({ theme }) => theme.colors.primary.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  }
`;

export const WizardCard = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[8]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  animation: ${slideUp} 0.3s ease-out;
`;

export const CardTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

export const CardDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgrounds.light};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[6]};

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.primary.base};
    flex-shrink: 0;
  }
`;

export const InfoRowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const InfoRowLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

export const InfoRowValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const RequiredStar = styled.span`
  color: ${({ theme }) => theme.colors.status.error.text};
  margin-left: 2px;
`;

export const Textarea = styled.textarea<{ $hasError: boolean }>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid
    ${({ $hasError, theme }) =>
      $hasError ? theme.colors.status.error.text : theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  resize: vertical;
  min-height: 96px;
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }

  &:focus {
    outline: none;
    border-color: ${({ $hasError, theme }) =>
      $hasError ? theme.colors.status.error.text : theme.colors.primary.base};
    box-shadow: ${({ $hasError, theme }) =>
      $hasError ? theme.shadows.ringError : theme.shadows.ring};
  }
`;

export const FieldError = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
`;

export const ErrorAlert = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

export const BottomActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[6]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

export const CenteredLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  min-height: 300px;
`;

export const LoaderText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;
